import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useAuth } from './AuthContext'
import { useWebSocket } from './WebSocketContext'
import { API_BASE_URL } from '../config/api'

const NotificationContext = createContext()

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

// --- Internal Hook for API Handling ---
function useApi(session) {
    const pendingRequests = useRef(new Map())

    const request = useCallback(async (endpoint, options = {}) => {
        const method = options.method || 'GET'
        const bodyStr = options.body ? JSON.stringify(options.body) : ''
        const requestKey = `${method}:${endpoint}:${bodyStr}`

        if (method === 'GET' && pendingRequests.current.has(requestKey)) {
            return pendingRequests.current.get(requestKey)
        }

        const execute = async () => {
            const url = `${API_BASE_URL}/api/v1${endpoint}`
            const headers = {
                'Content-Type': 'application/json',
                ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
            }

            const config = {
                ...options,
                headers: { ...headers, ...options.headers },
                body: options.body // Caller handles stringify if needed, or we do it here if object
            }

            if (config.body && typeof config.body !== 'string') {
                config.body = JSON.stringify(config.body)
            }

            const response = await fetch(url, config)
            
            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || `Request failed: ${response.status}`)
            }

            // Some endpoints might return 204 No Content
            if (response.status === 204) return null
            
            return response.json()
        }

        const promise = execute().finally(() => {
            if (method === 'GET') pendingRequests.current.delete(requestKey)
        })

        if (method === 'GET') {
            pendingRequests.current.set(requestKey, promise)
        }

        return promise
    }, [session])

    return request
}

export function NotificationProvider({ children }) {
  const { session } = useAuth()
  const { subscribe } = useWebSocket()
  const api = useApi(session)
  
  const [notifications, setNotifications] = useState([])
  
  // Helper for UI feedback
  const showToast = useCallback((message, type = 'info') => {
      if (window.showToast) window.showToast(message, type)
      else console.log(`TOAST [${type}]: ${message}`)
  }, [])

  const addNotification = useCallback((notification) => {
    setNotifications(prev => {
        // Dedup based on ID
        if (prev.some(n => n.id === notification.id)) return prev
        return [...prev, notification]
    })
  }, [])

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id))
  }, [])

  // WebSocket Subscription
  useEffect(() => {
      const handleNotification = (message) => {
          if (message.type !== 'notification' || !message.data) return

          const data = message.data
          const type = data.type
          
          // Generate stable ID if missing
          const id = data.id || `${type}_${Date.now()}`

          // Interactive notifications
          if (['challenge', 'game_invite', 'tournament'].includes(type)) {
              addNotification({
                  id,
                  type,
                  title: data.title || 'Chess Challenge!',
                  message: data.message || 'You have received a challenge',
                  hasActions: true,
                  data: data.data || data 
              })
          } else {
              // Passive notifications
              addNotification({
                  id,
                  type,
                  title: data.title,
                  message: data.message,
                  hasActions: false
              })
              // Also show toast for passive ones
              showToast(data.message || 'New notification', 'info')
          }
      }

      // Use modern subscribe
      const unsubscribe = subscribe('notification', handleNotification)
      return () => unsubscribe()
  }, [subscribe, addNotification, showToast])


  // --- Actions ---

  const handleAction = useCallback(async (actionName, apiCallFn, successMsg, eventToDispatch) => {
      try {
          const result = await apiCallFn()
          if (successMsg) showToast(successMsg, 'success')
          if (eventToDispatch) {
            // Support both old custom events and new navigation
             if (eventToDispatch.type === 'navigate-to-game' && window.navigateToGame) {
                 window.navigateToGame(eventToDispatch.detail.gameId)
             } else {
                 window.dispatchEvent(new CustomEvent(eventToDispatch.type, { detail: eventToDispatch.detail }))
             }
          }
          return result
      } catch (err) {
          console.error(`Error in ${actionName}:`, err)
          showToast(`Failed: ${err.message}`, 'error')
          throw err
      }
  }, [showToast])

  const acceptChallenge = useCallback((notification) => {
      const gameId = notification.data?.game_id
      if (!gameId) return
      
      return handleAction(
          'acceptChallenge',
          () => api(`/games/${gameId}/join`, { method: 'PUT' }),
          'Challenge accepted! Starting game...',
          { type: 'navigate-to-game', detail: { gameId } }
      )
  }, [api, handleAction])

  const declineChallenge = useCallback((notification) => {
      const gameId = notification.data?.game_id
      if (!gameId) return

      return handleAction(
          'declineChallenge',
          () => api(`/games/${gameId}/decline`, { method: 'POST' }),
          'Challenge declined',
          null
      )
  }, [api, handleAction])

  const acceptTournamentInvite = useCallback((notification) => {
      const tournamentId = notification.data?.tournament_id || notification.data?.id
      if (!tournamentId) return

      return handleAction(
          'acceptTournamentInvite',
          () => api(`/tournaments/${tournamentId}/join`, { method: 'POST' }),
          'Joined tournament successfully!',
          { type: 'navigate-to-tournament', detail: { tournamentId } }
      )
  }, [api, handleAction])

  const declineTournamentInvite = useCallback((notification) => {
      const tournamentId = notification.data?.tournament_id || notification.data?.id
      if (!tournamentId) return

      return handleAction(
          'declineTournamentInvite',
          () => api(`/tournaments/${tournamentId}/invite/decline`, { method: 'POST' }),
          'Tournament invitation declined',
          null
      )
  }, [api, handleAction])

  const acceptFriendRequest = useCallback((notification) => {
      const requestId = notification.data?.request_id || notification.data?.id
      if (!requestId) return

      return handleAction(
          'acceptFriendRequest',
          () => api(`/friends/requests/${requestId}/accept`, { method: 'POST' }),
          'Friend request accepted!',
          { type: 'friend-request-updated', detail: {} }
      )
  }, [api, handleAction])

  const declineFriendRequest = useCallback((notification) => {
      const requestId = notification.data?.request_id || notification.data?.id
      if (!requestId) return

      return handleAction(
          'declineFriendRequest',
          () => api(`/friends/requests/${requestId}/decline`, { method: 'POST' }),
          'Friend request declined',
          { type: 'friend-request-updated', detail: {} }
      )
  }, [api, handleAction])

  const value = useMemo(() => ({
    notifications,
    addNotification,
    removeNotification,
    acceptChallenge,
    declineChallenge,
    acceptTournamentInvite,
    declineTournamentInvite,
    acceptFriendRequest,
    declineFriendRequest
  }), [
    notifications, addNotification, removeNotification,
    acceptChallenge, declineChallenge,
    acceptTournamentInvite, declineTournamentInvite,
    acceptFriendRequest, declineFriendRequest
  ])

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}